import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';
import { SignedImage } from '../global/storageUrls';

// Field groups for the details modal — mirrors the layout of each form
// so the modal stays in sync as new fields are added.
const CHECK_IN_SECTIONS = [
  {
    title: 'Submitted By & Crew',
    fields: [
      { key: 'user_name', label: 'Submitted By' },
      { key: 'email', label: 'Email' },
      { key: 'oncoming_crew', label: 'Oncoming Crew' },
    ],
  },
  {
    title: 'Truck & Dispatch',
    fields: [
      { key: 'ambulance', label: 'Ambulance' },
      { key: 'dispatch_radio_check', label: 'Dispatch Radio Check' },
    ],
  },
  {
    title: 'Fuel & Vehicle',
    fields: [
      { key: 'fuel_card_present', label: 'Fuel Card Present' },
      { key: 'fuel_level', label: 'Fuel Level' },
      { key: 'def_full', label: 'DEF Full' },
      { key: 'tire_condition', label: 'Tire Condition' },
      { key: 'odometer_reading', label: 'Odometer' },
    ],
  },
  {
    title: 'Oxygen Levels',
    fields: [
      { key: 'main_oxygen_level', label: 'Main' },
      { key: 'stretcher_oxygen_level', label: 'Stretcher' },
      { key: 'spare_oxygen_1', label: 'Spare 1' },
      { key: 'spare_oxygen_2', label: 'Spare 2' },
    ],
  },
  {
    title: 'Equipment',
    fields: [
      { key: 'pediatric_bag_tag', label: 'Pediatric Bag Tag #' },
      { key: 'glucometer_high_test', label: 'Glucometer High Reading' },
      { key: 'glucometer_low_test', label: 'Glucometer Low Reading' },
      { key: 'ventilator_number', label: 'Ventilator #' },
      { key: 'ventilator_passed', label: 'Ventilator Passed' },
      { key: 'zoll_monitor', label: 'Zoll Monitor' },
      { key: 'defib_30j_test', label: 'Defib 30J Test' },
      { key: 'jump_bag_present', label: 'Jump Bag Present' },
      { key: 'stair_chair_battery', label: 'Stair Chair Battery' },
      { key: 'lucas_device_complete', label: 'LUCAS Device Complete' },
    ],
  },
  {
    title: 'IV Pumps',
    fields: [
      { key: 'iv_pumps_count', label: '# of Pumps' },
      { key: 'pump_1_number', label: 'Pump 1 #' },
      { key: 'pump_2_number', label: 'Pump 2 #' },
      { key: 'pump_3_number', label: 'Pump 3 #' },
      { key: 'pump_4_number', label: 'Pump 4 #' },
    ],
  },
];

const CHECK_OUT_SECTIONS = [
  {
    title: 'Submitted By',
    fields: [
      { key: 'submitted_by_first_name', label: 'First Name' },
      { key: 'submitted_by_last_name', label: 'Last Name' },
      { key: 'submitted_by_email', label: 'Email' },
    ],
  },
  {
    title: 'Truck Condition',
    fields: [
      { key: 'ambulance', label: 'Ambulance' },
      { key: 'truck_refueled', label: 'Truck Refueled' },
      { key: 'interior_cleaned', label: 'Interior Cleaned' },
      { key: 'exterior_cleaned', label: 'Exterior Cleaned' },
    ],
  },
  {
    title: 'Mechanical & Keys',
    fields: [
      { key: 'mechanical_concerns', label: 'Mechanical Concerns' },
      { key: 'mechanical_concerns_who', label: 'Notified' },
      { key: 'keys_location', label: 'Keys Location' },
      { key: 'keys_next_shift_crew', label: 'Next Shift Crew' },
    ],
  },
  {
    title: 'IV Pumps',
    fields: [
      { key: 'iv_pumps_count', label: '# of Pumps' },
      { key: 'pump_1_number', label: 'Pump 1 #' },
      { key: 'pump_2_number', label: 'Pump 2 #' },
      { key: 'pump_3_number', label: 'Pump 3 #' },
      { key: 'pump_4_number', label: 'Pump 4 #' },
    ],
  },
  {
    title: 'Crew Sign-Off',
    fields: [
      { key: 'crew_member_1_name', label: 'Crew Member 1' },
      { key: 'crew_member_1_signature_url', label: 'Crew 1 Signature', type: 'signature' },
      { key: 'crew_member_2_name', label: 'Crew Member 2' },
      { key: 'crew_member_2_license', label: 'Crew 2 License' },
      { key: 'crew_member_2_signature_url', label: 'Crew 2 Signature', type: 'signature' },
    ],
  },
];

function ShiftReconciliationReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAmbulance, setSelectedAmbulance] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [reconciliationData, setReconciliationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'checkInTime', direction: 'desc' });
  const [stats, setStats] = useState({
    total: 0,
    matched: 0,
    missingCheckout: 0,
    missingCheckin: 0
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const ambulances = ['Mayo 21', 'Mayo 22', 'Mayo 23', 'Mayo 24', 'Mayo 25'];

  // Set default date range to last 7 days
  useEffect(() => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(weekAgo.toISOString().split('T')[0]);
  }, []);

  const runReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setReconciliationData([]);

    try {
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');

      const { data: checkInData, error: checkInError } = await supabase
        .from('daily_truck_checks')
        .select('*')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())
        .order('created_at', { ascending: true });

      if (checkInError) throw checkInError;

      const extendedEnd = new Date(end);
      extendedEnd.setDate(extendedEnd.getDate() + 1);

      const { data: checkOutData, error: checkOutError } = await supabase
        .from('truck_checkout_checks')
        .select('*')
        .gte('created_at', start.toISOString())
        .lte('created_at', extendedEnd.toISOString())
        .order('created_at', { ascending: true });

      if (checkOutError) throw checkOutError;

      const reconciled = reconcileShifts(checkInData || [], checkOutData || []);
      
      let filtered = reconciled;
      if (selectedAmbulance !== 'all') {
        filtered = reconciled.filter(r => r.ambulance === selectedAmbulance);
      }

      const matched = filtered.filter(r => r.status === 'matched').length;
      const missingCheckout = filtered.filter(r => r.status === 'missing-checkout').length;
      const missingCheckin = filtered.filter(r => r.status === 'missing-checkin').length;

      setStats({ total: filtered.length, matched, missingCheckout, missingCheckin });
      setReconciliationData(filtered);

      if (filtered.length === 0) {
        alert('No records found for the selected criteria.');
      }
    } catch (err) {
      console.error('Error running report:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const reconcileShifts = (checkIns, checkOuts) => {
    const results = [];
    const usedCheckOuts = new Set();

    checkIns.forEach(checkIn => {
      const checkInTime = new Date(checkIn.created_at);
      const checkInAmbulance = checkIn.ambulance;

      const matchingCheckOut = checkOuts.find(checkOut => {
        if (usedCheckOuts.has(checkOut.id)) return false;
        if (checkOut.ambulance !== checkInAmbulance) return false;
        const checkOutTime = new Date(checkOut.created_at);
        const hoursDiff = (checkOutTime - checkInTime) / (1000 * 60 * 60);
        return hoursDiff >= 2 && hoursDiff <= 26;
      });

      if (matchingCheckOut) {
        usedCheckOuts.add(matchingCheckOut.id);
        results.push({
          id: `${checkIn.id}-${matchingCheckOut.id}`,
          status: 'matched',
          ambulance: checkInAmbulance,
          checkInTime: checkInTime,
          checkInCrew: checkIn.oncoming_crew || checkIn.user_name || `${checkIn.first_name} ${checkIn.last_name}`,
          checkInOdometer: checkIn.odometer_reading,
          checkInFuel: checkIn.fuel_level,
          checkOutTime: new Date(matchingCheckOut.created_at),
          checkOutCrew: `${matchingCheckOut.crew_member_1_name}, ${matchingCheckOut.crew_member_2_name}`,
          checkOutRefueled: matchingCheckOut.truck_refueled,
          checkOutKeysLocation: matchingCheckOut.keys_location,
          checkOutInteriorCleaned: matchingCheckOut.interior_cleaned,
          checkOutExteriorCleaned: matchingCheckOut.exterior_cleaned,
          mechanicalConcerns: matchingCheckOut.mechanical_concerns,
          mechanicalConcernsWho: matchingCheckOut.mechanical_concerns_who,
          shiftDuration: calculateDuration(checkInTime, new Date(matchingCheckOut.created_at)),
          checkInData: checkIn,
          checkOutData: matchingCheckOut
        });
      } else {
        results.push({
          id: checkIn.id,
          status: 'missing-checkout',
          ambulance: checkInAmbulance,
          checkInTime: checkInTime,
          checkInCrew: checkIn.oncoming_crew || checkIn.user_name || `${checkIn.first_name} ${checkIn.last_name}`,
          checkInOdometer: checkIn.odometer_reading,
          checkInFuel: checkIn.fuel_level,
          checkOutTime: null,
          checkOutCrew: null,
          shiftDuration: null,
          checkInData: checkIn,
          checkOutData: null
        });
      }
    });

    checkOuts.forEach(checkOut => {
      if (usedCheckOuts.has(checkOut.id)) return;
      const checkOutTime = new Date(checkOut.created_at);
      const startRange = new Date(startDate + 'T00:00:00');
      const endRange = new Date(endDate + 'T23:59:59');
      
      if (checkOutTime >= startRange && checkOutTime <= endRange) {
        results.push({
          id: checkOut.id,
          status: 'missing-checkin',
          ambulance: checkOut.ambulance,
          checkInTime: null,
          checkInCrew: null,
          checkOutTime: checkOutTime,
          checkOutCrew: `${checkOut.crew_member_1_name}, ${checkOut.crew_member_2_name}`,
          checkOutRefueled: checkOut.truck_refueled,
          checkOutKeysLocation: checkOut.keys_location,
          checkOutInteriorCleaned: checkOut.interior_cleaned,
          checkOutExteriorCleaned: checkOut.exterior_cleaned,
          mechanicalConcerns: checkOut.mechanical_concerns,
          mechanicalConcernsWho: checkOut.mechanical_concerns_who,
          shiftDuration: null,
          checkInData: null,
          checkOutData: checkOut
        });
      }
    });

    results.sort((a, b) => {
      const timeA = a.checkInTime || a.checkOutTime;
      const timeB = b.checkInTime || b.checkOutTime;
      return timeB - timeA;
    });

    return results;
  };

  const calculateDuration = (start, end) => {
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...reconciliationData].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === 'checkInTime' || key === 'checkOutTime') {
        aVal = aVal ? aVal.getTime() : 0;
        bVal = bVal ? bVal.getTime() : 0;
      }
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReconciliationData(sorted);
  };

  const getFilteredData = () => {
    if (filterStatus === 'all') return reconciliationData;
    return reconciliationData.filter(r => r.status === filterStatus);
  };

  const exportToCSV = () => {
    const data = getFilteredData();
    if (data.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Status', 'Ambulance', 'Check-In Date', 'Check-In Time', 'Check-In Crew', 'Check-Out Date', 'Check-Out Time', 'Check-Out Crew', 'Shift Duration', 'Mechanical Concerns'];
    const csvRows = [headers.join(',')];

    data.forEach(record => {
      const row = [
        record.status,
        record.ambulance,
        record.checkInTime ? record.checkInTime.toLocaleDateString() : '',
        record.checkInTime ? record.checkInTime.toLocaleTimeString() : '',
        record.checkInCrew || '',
        record.checkOutTime ? record.checkOutTime.toLocaleDateString() : '',
        record.checkOutTime ? record.checkOutTime.toLocaleTimeString() : '',
        record.checkOutCrew || '',
        record.shiftDuration || '',
        record.mechanicalConcerns === 'Yes' ? `Yes - ${record.mechanicalConcernsWho}` : 'No'
      ];
      csvRows.push(row.map(cell => `"${cell}"`).join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shift_reconciliation_${startDate}_to_${endDate}.csv`;
    link.click();
  };

  const handleRowClick = (record) => {
    setSelectedRecord(record);
    setShowDetailsModal(true);
  };

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Render the configured field-groups for a check-in or check-out record,
  // skipping fields with no value so the modal stays compact.
  const renderSections = (sections, data) => {
    if (!data) return null;
    return sections.map((section) => {
      const populated = section.fields.filter(({ key }) => {
        const v = data[key];
        return v !== null && v !== undefined && String(v).trim() !== '';
      });
      if (populated.length === 0) return null;
      return (
        <div key={section.title} style={{ marginTop: '12px' }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            {section.title}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {populated.map(({ key, label, type }) => (
              <div key={key}>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>{label}</div>
                {type === 'signature' ? (
                  <SignedImage
                    value={data[key]}
                    alt={label}
                    style={{
                      maxWidth: '180px',
                      maxHeight: '70px',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      background: '#fff',
                      marginTop: '4px'
                    }}
                  />
                ) : (
                  <div style={{ fontWeight: '500', wordBreak: 'break-word' }}>{String(data[key])}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  // Table styles
  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#495057',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #dee2e6',
    background: '#f8f9fa',
    cursor: 'pointer',
    userSelect: 'none'
  };

  const tdStyle = {
    padding: '10px 12px',
    verticalAlign: 'middle',
    borderBottom: '1px solid #e9ecef',
    fontSize: '13px',
    color: '#212529'
  };

  return (
    <div className="container">
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div className="content" style={{ alignItems: 'stretch', maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
        <h1 className="title" style={{ textAlign: 'center' }}>Shift Reconciliation Report</h1>
        <p className="subtitle" style={{ textAlign: 'center' }}>Match check-ins with check-outs and identify missing entries</p>

        {/* Filters */}
        <div className="card" style={{ marginBottom: '24px', maxWidth: 'none' }}>
          <h3 className="section-title">Report Filters</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label className="form-label">Start Date</label>
              <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="form-label">End Date</label>
              <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Ambulance</label>
              <select className="form-input" value={selectedAmbulance} onChange={(e) => setSelectedAmbulance(e.target.value)}>
                <option value="all">All Ambulances</option>
                {ambulances.map(amb => <option key={amb} value={amb}>{amb}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="matched">Complete</option>
                <option value="missing-checkout">Missing Check-Out</option>
                <option value="missing-checkin">Missing Check-In</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-primary" onClick={runReport} disabled={loading}>
              {loading ? 'Loading...' : 'Run Report'}
            </button>
            {reconciliationData.length > 0 && (
              <button className="btn btn-secondary" onClick={exportToCSV}>Export CSV</button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        {reconciliationData.length > 0 && (
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <div style={{ flex: 1, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#0E3293' }}>{stats.total}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>Total Shifts</div>
            </div>
            <div style={{ flex: 1, background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', padding: '16px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#155724' }}>{stats.matched}</div>
              <div style={{ fontSize: '12px', color: '#155724', marginTop: '4px' }}>Complete</div>
            </div>
            <div style={{ flex: 1, background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '16px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#721c24' }}>{stats.missingCheckout}</div>
              <div style={{ fontSize: '12px', color: '#721c24', marginTop: '4px' }}>No Check-Out</div>
            </div>
            <div style={{ flex: 1, background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '8px', padding: '16px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#856404' }}>{stats.missingCheckin}</div>
              <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>No Check-In</div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {getFilteredData().length > 0 && (
          <div className="card" style={{ padding: '0', overflow: 'hidden', maxWidth: 'none' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e9ecef', background: '#fff' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#212529' }}>
                Results ({getFilteredData().length})
              </h3>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle} onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</th>
                    <th style={{ ...thStyle, textAlign: 'center' }} onClick={() => handleSort('ambulance')}>Truck{getSortIndicator('ambulance')}</th>
                    <th style={thStyle} onClick={() => handleSort('checkInTime')}>Check-In{getSortIndicator('checkInTime')}</th>
                    <th style={thStyle}>Crew In</th>
                    <th style={thStyle} onClick={() => handleSort('checkOutTime')}>Check-Out{getSortIndicator('checkOutTime')}</th>
                    <th style={thStyle}>Crew Out</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Duration</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredData().map((record, index) => {
                    const rowBg = record.status === 'missing-checkout' 
                      ? '#fff5f5' 
                      : record.status === 'missing-checkin' 
                        ? '#fffef0' 
                        : index % 2 === 0 ? '#fff' : '#f8f9fa';
                    
                    return (
                      <tr 
                        key={record.id} 
                        style={{ background: rowBg, cursor: 'pointer', transition: 'background 0.15s' }}
                        onClick={() => handleRowClick(record)}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#e8f4ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = rowBg}
                      >
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: record.status === 'matched' ? '#d4edda' : record.status === 'missing-checkout' ? '#f8d7da' : '#fff3cd',
                            color: record.status === 'matched' ? '#155724' : record.status === 'missing-checkout' ? '#721c24' : '#856404'
                          }}>
                            {record.status === 'matched' ? 'Complete' : record.status === 'missing-checkout' ? 'No Out' : 'No In'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>
                          {record.ambulance?.replace('Mayo ', 'M')}
                        </td>
                        <td style={tdStyle}>
                          {record.checkInTime ? (
                            <div>
                              <div style={{ fontWeight: '500' }}>{formatDate(record.checkInTime)}</div>
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>{formatTime(record.checkInTime)}</div>
                            </div>
                          ) : <span style={{ color: '#adb5bd' }}>-</span>}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.checkInCrew || <span style={{ color: '#adb5bd' }}>-</span>}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {record.checkOutTime ? (
                            <div>
                              <div style={{ fontWeight: '500' }}>{formatDate(record.checkOutTime)}</div>
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>{formatTime(record.checkOutTime)}</div>
                            </div>
                          ) : <span style={{ color: '#adb5bd' }}>-</span>}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.checkOutCrew || <span style={{ color: '#adb5bd' }}>-</span>}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: record.shiftDuration ? '#212529' : '#adb5bd' }}>
                          {record.shiftDuration || '-'}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {record.mechanicalConcerns === 'Yes' && (
                            <span style={{ fontSize: '16px', cursor: 'pointer' }} title="Mechanical concerns reported">⚠️</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {reconciliationData.length === 0 && !loading && (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 'none' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>No Data Yet</h3>
            <p style={{ color: '#666' }}>Select a date range and click "Run Report" to view shift reconciliation data.</p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRecord && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '820px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#212529' }}>Shift Details</h2>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6c757d' }}>
                  {selectedRecord.ambulance} - {selectedRecord.checkInTime ? formatDate(selectedRecord.checkInTime) : formatDate(selectedRecord.checkOutTime)}
                </p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Status Banner */}
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                background: selectedRecord.status === 'matched' ? '#d4edda' : selectedRecord.status === 'missing-checkout' ? '#f8d7da' : '#fff3cd',
                color: selectedRecord.status === 'matched' ? '#155724' : selectedRecord.status === 'missing-checkout' ? '#721c24' : '#856404'
              }}>
                <strong>
                  {selectedRecord.status === 'matched' ? '✓ Complete Shift' : selectedRecord.status === 'missing-checkout' ? '⚠ Missing Check-Out' : '⚠ Missing Check-In'}
                </strong>
                {selectedRecord.shiftDuration && <span style={{ marginLeft: '12px' }}>Duration: {selectedRecord.shiftDuration}</span>}
              </div>

              {/* Check-In Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Check-In Details
                </h4>
                {selectedRecord.checkInData ? (
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>Submitted</div>
                      <div style={{ fontWeight: '500' }}>{selectedRecord.checkInTime.toLocaleString()}</div>
                    </div>
                    {renderSections(CHECK_IN_SECTIONS, selectedRecord.checkInData)}
                  </div>
                ) : (
                  <div style={{ background: '#fff3cd', borderRadius: '8px', padding: '16px', color: '#856404' }}>
                    No check-in record found for this shift
                  </div>
                )}
              </div>

              {/* Check-Out Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Check-Out Details
                </h4>
                {selectedRecord.checkOutData ? (
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>Submitted</div>
                      <div style={{ fontWeight: '500' }}>{selectedRecord.checkOutTime.toLocaleString()}</div>
                    </div>
                    {renderSections(CHECK_OUT_SECTIONS, selectedRecord.checkOutData)}
                  </div>
                ) : (
                  <div style={{ background: '#f8d7da', borderRadius: '8px', padding: '16px', color: '#721c24' }}>
                    No check-out record found for this shift
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e9ecef', textAlign: 'right' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <p className="text-small">NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC</p>
      </footer>
    </div>
  );
}

export default ShiftReconciliationReport;
